import * as React from 'react';
import { Accordion as ChakraAccordion } from '@chakra-ui/react';
import { useAccordionItemContext } from '@chakra-ui/react';

/**
 * Project Accordion.
 *
 * NB-shaped facade over Chakra v3's `Accordion` compositional API:
 *   - NB `allowMultiple` -> Chakra `multiple`
 *   - NB numeric `index` -> Chakra string `defaultValue`
 *   - `Accordion.Item` accepts render-prop children exposing
 *     `isExpanded`, sourced from Chakra's `ItemContext`
 *   - Summary -> `Accordion.ItemTrigger`
 *   - Details -> `Accordion.ItemContent`
 *   - Icon -> `Accordion.ItemIndicator`
 */

type RootProps = {
  allowMultiple?: boolean;
  index?: (number | null | undefined)[];
  children: React.ReactNode;
  [key: string]: any;
};

const AccordionRoot = ({
  allowMultiple,
  index,
  children,
  ...rest
}: RootProps) => {
  const defaultValue = (index ?? [])
    .filter((v): v is number => typeof v === 'number')
    .map(String);
  return (
    <ChakraAccordion.Root
      multiple={allowMultiple}
      defaultValue={defaultValue}
      {...rest}
    >
      {children}
    </ChakraAccordion.Root>
  );
};

type ItemProps = {
  index: number;
  children:
    | ((details: { isExpanded: boolean }) => React.ReactNode)
    | React.ReactNode;
};

const RenderProp = ({
  render,
}: {
  render: (d: { isExpanded: boolean }) => React.ReactNode;
}) => {
  const ctx = useAccordionItemContext();
  return <>{render({ isExpanded: ctx.open })}</>;
};

const Item = ({ index, children }: ItemProps) => (
  <ChakraAccordion.Item value={String(index)}>
    {typeof children === 'function' ? (
      <RenderProp
        render={children as (d: { isExpanded: boolean }) => React.ReactNode}
      />
    ) : (
      children
    )}
  </ChakraAccordion.Item>
);

export const Accordion = Object.assign(AccordionRoot, {
  Item,
  Summary: ChakraAccordion.ItemTrigger,
  Details: ChakraAccordion.ItemContent,
  Icon: ChakraAccordion.ItemIndicator,
});
